import { impressionConfig } from "./config";

/**
 * Global scroll-velocity tracker for the fling gate. One capture-phase listener catches scroll
 * from any scroller (events don't bubble, but capture reaches ancestors), so we don't care whether
 * the feed scrolls the window or an inner element. `isFlinging()` reports whether the user is
 * currently scrolling fast enough that on-screen posts are being flung past, not read; when scroll
 * settles, registered sweep callbacks run so still-visible posts can be credited.
 *
 * Before `startScrollTracking()` runs (and in unit tests, which drive the episode machine directly
 * with no scroll events), velocity is 0 and `isFlinging()` is false — nothing is treated as a fling.
 */

let lastY: number | null = null;
let lastT = 0;
let velocity = 0; // px per ms
let settleTimer: ReturnType<typeof setTimeout> | undefined;
let started = false;

const settleCallbacks = new Set<() => void>();

const scrollYOf = (target: EventTarget | null): number => {
  if (!target || target === document || target === window) return window.scrollY;
  const el = target as { scrollTop?: number };
  return typeof el.scrollTop === "number" ? el.scrollTop : window.scrollY;
};

const onSettle = (): void => {
  velocity = 0;
  lastY = null;
  settleTimer = undefined;
  for (const cb of settleCallbacks) cb();
};

const handleScroll = (event: Event): void => {
  const now = Date.now();
  const y = scrollYOf(event.target);
  if (lastY !== null && now > lastT) {
    velocity = Math.abs(y - lastY) / (now - lastT);
  }
  lastY = y;
  lastT = now;

  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(onSettle, impressionConfig.scrollSettleMs);
};

/** True while scrolling faster than the fling threshold — samples taken now aren't a read. */
const isFlinging = (): boolean =>
  velocity > impressionConfig.flingVelocityPxPerMs;

/** Register a callback to run once scrolling settles (the post-scroll sweep). */
const onScrollSettled = (cb: () => void): void => {
  settleCallbacks.add(cb);
};

const startScrollTracking = (): void => {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("scroll", handleScroll, {
    passive: true,
    capture: true,
  });
};

export { isFlinging, onScrollSettled, startScrollTracking };
